import React from 'react';
import { MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../../services/apiClient';
import { getFullMediaUrl } from '../../../utils/urlHelper';

interface Gym {
  _id: string;
  name: string;
  code: string;
  phone: string;
  address: string;
  city: string;
  photoUrl: string;
  timezone: string;
  description?: string;
  facilities?: string[];
  operatingHours?: any[];
  coverPhoto?: string;
}

interface GymCardProps {
  gym: Gym;
  onViewDetails: (gymId: string) => void;
}

const GymCard: React.FC<GymCardProps> = ({ gym, onViewDetails }) => {
  // Fetch trainers count
  const { data: trainersData } = useQuery({
    queryKey: ['gym-trainers', gym._id],
    queryFn: async () => {
      const response = await apiClient.get(`/gyms/${gym._id}/trainers`);
      return response.data?.data || response.data || { count: 0 };
    },
  });

  // Fetch members count
  const { data: membersData } = useQuery({
    queryKey: ['gym-members', gym._id],
    queryFn: async () => {
      const response = await apiClient.get(`/gyms/${gym._id}/members`);
      return response.data?.data || response.data || { count: 0 };
    },
  });

  const trainersCount = trainersData?.count || 0;
  const membersCount = membersData?.count || 0;
  return (
    <div
      onClick={() => onViewDetails(gym._id)}
      className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden hover:border-[#23F47C] transition-all cursor-pointer group"
    >
      {/* Cover Photo */}
      <div className="h-48 bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden relative">
        {gym.coverPhoto ? (
          <img
            src={gym.coverPhoto}
            alt={gym.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : gym.photoUrl ? (
          <img
            src={getFullMediaUrl(gym.photoUrl) || ''}
            alt={gym.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600">
            <span>No image</span>
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-3 right-3 bg-[#23F47C] text-gray-900 px-3 py-1 rounded-full text-xs font-semibold">
          ACTIVE
        </div>

        {/* Gym Icon */}
        <div className="absolute top-3 left-3 bg-[#23F47C] bg-opacity-20 text-[#23F47C] w-10 h-10 rounded-full flex items-center justify-center">
          💪
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-white mb-1">{gym.name}</h3>
        <p className="text-[#23F47C] text-sm font-semibold mb-3">CODE: {gym.code}</p>

        <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
          <MapPin size={16} />
          <span>{gym.address}</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4 pt-4 border-t border-gray-800">
          <div>
            <p className="text-gray-500 text-xs uppercase mb-1">Members</p>
            <p className="text-white font-bold text-lg">{membersCount}</p>
          </div>
          <div>
            <p className="text-gray-500 text-xs uppercase mb-1">Trainers</p>
            <p className="text-white font-bold text-lg">{trainersCount}</p>
          </div>
        </div>

        {/* View Details Button */}
        <button className="w-full bg-gray-800 hover:bg-gray-700 text-[#23F47C] border border-gray-700 hover:border-[#23F47C] px-4 py-2 rounded-lg font-semibold transition-all">
          View Details
        </button>
      </div>
    </div>
  );
};

export default GymCard;
